import {
  fork,
  put,
  all,
  call,
  select,
  take,
  cancel,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';
import rsf from '../../helpers/firebase';
import i18n from '../../i18n';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);
const functions = getFunctions(rsf.app);

//connectFunctionsEmulator(functions, '127.0.0.1', 5001);

const normalizeDesign = (design) => {
  if (!design) return design;
  if (typeof design !== 'string') return design;

  try {
    return JSON.parse(design);
  } catch (error) {
    return design;
  }
};


const demTransformer = (payload) => {
  const dems = [];
  payload.forEach((d) => {
    const data = d.data();
    dems.push({
      id: d.id,
      ...data,
      design: normalizeDesign(data.design),
      createdAt: toDateFirebase(d, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(d, data, 'updatedAt').toDate(),
      }),
    });
  });
  return dems;
};

function* syncDemsSaga() {
  const companyId = yield select((state) => state.Dashboard.companyId);
  const demsRef = query(
    collection(db, 'dems'),
    where('companyId', '==', companyId),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, demsRef, {
    successActionCreator: actions.syncDemsSuccess,
    failureActionCreator: actions.syncDemsFailure,
    transform: (payload) => demTransformer(payload),
  });

  yield take(types.RESET_DEMS_STATE);
  yield cancel(task);
}

function* fetchDemSaga({ demId }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const docRef = doc(db, 'dems', demId);
    const snap = yield call(rsf.firestore.getDocument, docRef);

    if (!snap.exists) throw new Error('DEM not found');

    const data = snap.data();
    if (data.companyId !== companyId) throw new Error('DEM not found');

    const dem = {
      id: snap.id,
      ...data,
      design: normalizeDesign(data.design),
      createdAt: toDateFirebase(snap, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(snap, data, 'updatedAt').toDate(),
      }),
    };

    yield put(actions.fetchDemSuccess(dem));
  } catch (error) {
    yield put(actions.fetchDemFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* saveDemSaga({ payload }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const saveFn = httpsCallable(functions, 'demSave-save');

    const body = {
      ...payload,
      companyId,
    };

    const { data } = yield call(saveFn, body);
    if (data.error) throw new Error(data.error.message);

    yield put(actions.saveDemSuccess());
    toastr.success(i18n.t('DEM saved and published'), '');
  } catch (error) {
    yield put(actions.saveDemFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* archiveDemSaga({ demId }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const saveFn = httpsCallable(functions, 'demSave-save');
    const { data } = yield call(saveFn, {
      demId,
      companyId,
      archived: true,
    });
    if (data.error) throw new Error(data.error.message);

    yield put(actions.archiveDemSuccess());
    toastr.success(i18n.t('DEM archived'), '');
  } catch (error) {
    yield put(actions.archiveDemFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* resetDemStatsSaga({ demId }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);
    const docRef = doc(db, 'dems', demId);
    const snap = yield call(rsf.firestore.getDocument, docRef);

    if (!snap.exists) throw new Error('DEM not found');

    const data = snap.data();
    if (data.companyId !== companyId) throw new Error('DEM not found');

    yield call(
      rsf.firestore.setDocument,
      docRef,
      { stats: { clicks: 0, views: 0 } },
      { merge: true },
    );

    yield put(actions.resetDemStatsSuccess());
    toastr.success(i18n.t('DEM stats reset'), '');
  } catch (error) {
    yield put(actions.resetDemStatsFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* generateAiDemSaga({ prompt, currentDesign }) {
  try {
    const allMessages = yield select((state) => state.Dems.ai.messages);
    const history = allMessages
      .slice(0, -1)
      .map(({ role, content }) => ({ role, content }));

    const generateFn = httpsCallable(functions, 'generateAiDem-generateAiDem');
    const { data } = yield call(generateFn, { prompt, currentDesign, history });

    if (data.error) throw new Error(data.error.message);

    yield put(
      actions.generateAiDemSuccess({
        message: data.data.message,
        design: data.data.design,
        designSnapshotBefore: currentDesign || null,
      }),
    );
  } catch (error) {
    yield put(actions.generateAiDemFailure(error.message));
    toastr.error(i18n.t('Error during AI generation'), '');
  }
}

function* demsSaga() {
  yield all([
    takeLatest(types.SYNC_DEMS.REQUEST, syncDemsSaga),
    takeLeading(types.FETCH_DEM.REQUEST, fetchDemSaga),
    takeLeading(types.SAVE_DEM.REQUEST, saveDemSaga),
    takeLeading(types.ARCHIVE_DEM.REQUEST, archiveDemSaga),
    takeLeading(types.RESET_DEM_STATS.REQUEST, resetDemStatsSaga),
    takeLeading(types.GENERATE_AI_DEM.REQUEST, generateAiDemSaga),
  ]);
}

export default demsSaga;
